/**
 * Where a viewer's chosen market is stored, and whose choice it is.
 *
 * Deliberately dependency-free and separate from countryConfig.ts: the auth
 * layer has to forget the choice on sign-out, and countryConfig.ts imports
 * useAuth, so putting this there would make the two modules import each other.
 *
 * ── The failure this shape exists to prevent ────────────────────────────
 * This used to be a bare country code under the same key. A bare code belongs
 * to the DEVICE, so it outranked everything for everyone who ever used that
 * phone: a helper's app reported itself as another market entirely, and
 * offered only that market's cities in the booking flow, after one tap on the
 * unlabelled selector in Find Jobs — with no way back, and nothing on screen
 * saying why (owner report, 2026-08-26). It also survived sign-out, so on a
 * shared phone the next person inherited it.
 *
 * Attribution is what makes the choice safe to honour: it follows the person
 * who made it, never the device.
 */

export const COUNTRY_OVERRIDE_KEY = 'th-country-override';

/** Whose choice a signed-out selection is. */
export const ANON_VIEWER = 'anon';

export type StoredMarketChoice = { code: string; forUser: string };

export function readStoredMarketChoice(): StoredMarketChoice | null {
  try {
    const raw = localStorage.getItem(COUNTRY_OVERRIDE_KEY);
    if (!raw) return null;
    if (raw.startsWith('{')) {
      const parsed = JSON.parse(raw) as Partial<StoredMarketChoice>;
      return typeof parsed.code === 'string' && typeof parsed.forUser === 'string'
        ? { code: parsed.code, forUser: parsed.forUser }
        : null;
    }
    // A legacy bare code, written before choices were attributable. Read as a
    // SIGNED-OUT choice: it still applies while nobody is signed in, but it can
    // no longer outrank the country on a signed-in account. That is what heals
    // the devices already stuck in the wrong market, without discarding a
    // choice a signed-out visitor made on purpose.
    return { code: raw, forUser: ANON_VIEWER };
  } catch {
    return null;
  }
}

/**
 * ONE market choice, shared by every component that reads it.
 *
 * useUserCountryConfig is called from at least six places (the booking flow,
 * Post a Job, Find Jobs, saved addresses, the site-copy overrides, and the
 * market banner). Held as per-component React state, each of those keeps its
 * own copy: switching market in one updated that one and left the rest of the
 * app on the old market until it happened to remount. Caught on deployed
 * staging — "Switch to <home>" in the banner cleared the banner while the
 * page behind it still showed the other market's name (2026-08-26).
 *
 * A module-level value plus subscribers is what makes the choice a single
 * fact. `storage` events are included so a switch in one tab reaches the
 * others, which is the same expectation a signed-in session already sets.
 */
const listeners = new Set<() => void>();
let snapshot: StoredMarketChoice | null | undefined;

function notify(): void {
  for (const l of listeners) l();
}

export function subscribeToMarketChoice(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1 && typeof window !== 'undefined') {
    window.addEventListener('storage', onStorage);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('storage', onStorage);
    }
  };
}

function onStorage(e: StorageEvent): void {
  if (e.key !== null && e.key !== COUNTRY_OVERRIDE_KEY) return;
  snapshot = undefined; // re-read from storage on next getSnapshot
  notify();
}

/**
 * The current choice, cached.
 *
 * useSyncExternalStore compares snapshots by identity and re-renders forever
 * if a fresh object comes back each time, so the parsed value is held and only
 * replaced when it actually changes.
 */
export function getMarketChoiceSnapshot(): StoredMarketChoice | null {
  if (snapshot === undefined) snapshot = readStoredMarketChoice();
  return snapshot;
}

export function writeStoredMarketChoice(choice: StoredMarketChoice | null): void {
  try {
    if (choice) localStorage.setItem(COUNTRY_OVERRIDE_KEY, JSON.stringify(choice));
    else localStorage.removeItem(COUNTRY_OVERRIDE_KEY);
  } catch {
    /* localStorage unavailable (private mode, quota) — the app must still run */
  }
  snapshot = choice;
  notify();
}

/**
 * Forget the device's market choice. Called on sign-out, so the next person to
 * sign in on a shared phone does not inherit the previous one's market —
 * and with it their currency, city list and site copy.
 */
export function clearCountryOverride(): void {
  writeStoredMarketChoice(null);
}
